import React, { useCallback, useEffect, useMemo } from 'react';

import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { MediaDetail } from '@adsk/acc-sitevision-web-lib';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import Portal from '@adsk/alloy-react-portal';

import {
  UPDATE_PHOTO_TITLE,
  UPDATE_PHOTO,
  ADD_PHOTO_TAGS,
  REMOVE_PHOTO_TAGS,
  GET_PHOTO_DETAIL,
  GET_TAGS,
  GET_SETTINGS,
} from './helpers/gql/queries';
import PhotosServiceClient from './helpers/photosServiceClient';
import { FLAGS, isSettingActivated, SETTINGS } from './helpers/util';
import PhotoReferencesPlaceholder from './PhotoReferencesPlaceholder';
import { usePlangridFeatureFlag } from './PlangridLaunchDarklyProvider';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2000;
`;

const differenceByName = (a, b) =>
  a.filter((tag) => !b.map((otherTag) => otherTag.name).includes(tag.name));

const intersectionByName = (a, b) =>
  a.filter((tag) => b.map((otherTag) => otherTag.name).includes(tag.name));

const sortByName = (a, b) => {
  if (a.name.toLowerCase() < b.name.toLowerCase()) {
    return -1;
  }
  if (a.name.toLowerCase() > b.name.toLowerCase()) {
    return 1;
  }
  return 0;
};

const PhotoEntityPreviewModal = ({ entity, open, onClose, onUpdate }) => {
  const [editTitle] = useMutation(UPDATE_PHOTO_TITLE);
  const projectId = useCurrentProjectId();

  const { value: canAutotag } = usePlangridFeatureFlag(
    FLAGS.PHOTO_AUTOTAGGING,
    false,
  );

  // check for hide-autotag setting
  const { data: settings } = useQuery(GET_SETTINGS, {
    variables: { projectId },
  });
  const items = settings?.ggPhotoSettingsByProject?.items;
  const hideAutotags =
    items && isSettingActivated(items, SETTINGS.HIDE_AUTOTAGS);

  const showAutotags = canAutotag && !hideAutotags;

  const typesToShow = useMemo(() => {
    if (showAutotags) return ['USER', 'ML'];
    return ['USER'];
  }, [showAutotags]);

  const { data: tagData } = useQuery(GET_TAGS, {
    variables: { projectId, types: typesToShow },
  });

  const [addTags] = useMutation(ADD_PHOTO_TAGS);
  const [removeTags] = useMutation(REMOVE_PHOTO_TAGS);
  const [getPhotoDetail, { data: photoData }] = useLazyQuery(GET_PHOTO_DETAIL);
  const [updatePhoto] = useMutation(UPDATE_PHOTO);

  useEffect(() => {
    if (entity && entity.id) {
      getPhotoDetail({
        variables: {
          photoUid: entity.id,
          projectId,
          excludeAutotags: !showAutotags,
        },
      });
    }
  }, [getPhotoDetail, projectId, entity, showAutotags]);

  const photoDetailedData = photoData?.ggPhotoByUid;
  const tagOptions = tagData?.ggProjectByUid?.tags || [];

  const handleEditTitle = useCallback(
    (photo, title) => {
      return editTitle({
        variables: { input: { uid: photo.uid, title, projectUid: projectId } },
      }).then(({ data }) => {
        if (data && data.ggUpdatePhoto) {
          onUpdate({
            ...entity,
            title: data.ggUpdatePhoto.photo.title,
          });
        }
      });
    },
    [onUpdate, editTitle, entity, projectId],
  );

  const removeTagsHandler = useCallback(
    (photo, tags) => {
      const tagsWithoutTypename = tags.map(({ name, type }) => ({
        name,
        type,
      }));

      return removeTags({
        variables: {
          input: {
            projectUid: projectId,
            uid: photo.uid,
            tags: tagsWithoutTypename,
          },
        },
        update: (cache) => {
          const updatedTags = differenceByName(photo.tags, tags);
          // update tags for photo
          cache.writeQuery({
            query: GET_PHOTO_DETAIL,
            variables: {
              projectId,
              uid: photo.uid,
              excludeAutotags: !showAutotags,
            },
            data: {
              ggPhotoByUid: {
                ...photo,
                tags: updatedTags,
              },
            },
          });
        },
      });
    },
    [removeTags, projectId, showAutotags],
  );

  const addTagsHandler = useCallback(
    (photo, tags) => {
      const addedTags = differenceByName(tags, photo.tags);
      if (addedTags.length === 0) return Promise.resolve();

      return addTags({
        variables: {
          input: {
            projectUid: projectId,
            uid: photo.uid,
            tags: addedTags,
          },
        },
        update: (cache) => {
          // update tags for photo
          cache.writeQuery({
            query: GET_PHOTO_DETAIL,
            variables: {
              projectId,
              uid: photo.uid,
              excludeAutotags: !showAutotags,
            },
            data: {
              ggPhotoByUid: {
                ...photo,
                tags: photo.tags.concat(addedTags).sort(sortByName),
              },
            },
          });

          // update cache with newly created tags, if they exist
          const projectTags = cache.readQuery({
            query: GET_TAGS,
            variables: {
              projectId,
              types: typesToShow,
            },
          });

          const createdTags = differenceByName(
            addedTags,
            projectTags.ggProjectByUid.tags,
          );
          if (createdTags.length > 0) {
            cache.writeQuery({
              query: GET_TAGS,
              variables: { projectId, types: typesToShow },
              data: {
                ggProjectByUid: {
                  ...projectTags.ggProjectByUid,
                  tags: [
                    ...projectTags.ggProjectByUid.tags,
                    ...createdTags,
                  ].sort(sortByName),
                },
              },
            });
          }
        },
      });
    },
    [addTags, projectId, typesToShow, showAutotags],
  );

  const handleDownload = useCallback(() => {
    if (!(projectId && entity && entity.id)) return Promise.resolve();
    return PhotosServiceClient.assets
      .downloadOne(projectId, entity.id, 'full')
      .then((content) => {
        function download(url) {
          const a = document.createElement('a');
          a.href = url;
          a.download = url.split('/').pop();
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        download(content.url);
      });
  }, [projectId, entity]);

  const editLocationHandler = useCallback(
    (photo, locationId) => {
      updatePhoto({
        variables: {
          input: {
            uid: photo.uid,
            projectUid: projectId,
            locationId,
          },
        },
      });
    },
    [updatePhoto, projectId],
  );

  if (!open || !photoDetailedData) return null;
  return (
    <Portal container={document.body}>
      <Container
        // Prevents datadog from logging any PII contained within this element
        // Added here as a portal on document body cannot be wrapped by consumers
        data-dd-action-name="<MASKED-DATA>"
      >
        <MediaDetail
          editTitleHandler={
            photoDetailedData && !photoDetailedData.isLocked && handleEditTitle
          }
          downloadHandler={handleDownload}
          addTagsHandler={(photo, tags) => {
            const autotags = photo.tags.filter((t) => t.type === 'ML');
            const autotagsToRemove = intersectionByName(autotags, tags);
            // if tag is also an autotag, remove that tag
            const remove = Promise.resolve();
            if (autotagsToRemove.length > 0) {
              removeTagsHandler(photo, autotagsToRemove);
              photo.tags = differenceByName(photo.tags, autotagsToRemove);
            }

            remove
              .then(() => addTagsHandler(photo, tags))
              .catch((e) => console.error(e));
          }}
          removeTagsHandler={removeTagsHandler}
          editLocationHandler={editLocationHandler}
          projectTags={tagOptions}
          media={{
            ...photoDetailedData,
            uid: photoDetailedData.id,
          }}
          closeModalHandler={onClose}
          closeHandler={onClose}
          renderReferences={(p) => (
            <PhotoReferencesPlaceholder
              projectId={projectId}
              photoUid={p.media.uid}
            />
          )}
        />
      </Container>
    </Portal>
  );
};

PhotoEntityPreviewModal.propTypes = {
  entity: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdate: PropTypes.func,
};

export default PhotoEntityPreviewModal;
